// Pointer interactions for the MDS perceptual map.
// Port of BrandPerceptialMapping.Mds.ChartView mouseup / mouseover / mouseout / drag handlers
// (bpm/mds/chart.coffee). Attached imperatively to the drawn nodes; live state is read through
// refs so the handlers never go stale across React re-renders.

import * as d3 from 'd3'

import { CLS } from '@aytm/app/stats/page/question-views/bpm/components/mds/components/chart/lib/mdsClasses'

const TRANSLATE = /translate\(\s*(-?\d+(?:\.\d+)?)[\s,]+(-?\d+(?:\.\d+)?)/

const parseTranslate = transform => {
  const m = TRANSLATE.exec(transform || '')
  return m ? { x: Number(m[1]), y: Number(m[2]) } : { x: 0, y: 0 }
}

// Drag a label group; redraw its leader line. dx/dy accumulation is version-stable
// and reproduces the legacy "label follows cursor" behaviour.
const attachDrag = (root, dragRef) => {
  let base = { x: 0, y: 0 }

  const drag = d3
    .drag()
    .on('start', function () {
      base = parseTranslate(d3.select(this).attr('transform'))
    })
    .on('drag', function (event) {
      dragRef.current.dragging = true
      base = { x: base.x + event.dx, y: base.y + event.dy }

      const point = this.parentNode
      const circle = point.querySelector(`circle.${CLS.inner}`)
      const textEl = point.querySelector(`g.${CLS.label} text`)
      const leader = point.querySelector(`path.${CLS.pointPath}`)

      const x1 = Number(circle.getAttribute('cx'))
      const y1 = Number(circle.getAttribute('cy'))
      const x2 = base.x
      const y2 = base.y

      d3.select(this).attr('transform', `translate(${x2}, ${y2})`)

      if (leader) {
        const leaderY = y2 < y1 ? y2 + (textEl?.getBBox?.().height || 0) : y2
        leader.setAttribute('d', `M ${x1} ${y1} L ${x2} ${leaderY}`)
      }
    })
    .on('end', () => {
      dragRef.current.dragging = false
    })

  root.selectAll(`.${CLS.points} .${CLS.point} g.${CLS.label}`).call(drag)
}

const handleMouseUp = (point, { getSelected, stateRef, dragRef, defaultBrands, onBrandsChange }) => {
  // a drag just finished — swallow the click, don't toggle selection
  if (dragRef.current.dragging) {
    dragRef.current.dragging = false
    return
  }

  const selected = getSelected()
  const contains = selected.includes(point.id)
  let next

  if (!selected.length) {
    stateRef.current.selectedState = []
    next = [point.id]
  } else if (selected.length === 1 && contains) {
    // deselect the lone brand → restore the previous set (or all)
    next = stateRef.current.selectedState || defaultBrands
  } else {
    if (stateRef.current.selectedSingle === undefined) stateRef.current.selectedState = selected
    next = [point.id]
  }

  stateRef.current.selected = next
  onBrandsChange?.(next)
}

const attachPointHandlers = (root, ctx) => {
  const { points, isMobile, getSelected, stateRef, dragRef, defaultBrands, onBrandsChange } = ctx

  root.selectAll(`.${CLS.points} .${CLS.point}`).each(function (_, i) {
    const point = points[i]
    if (!point) return
    const node = d3.select(this)

    node.on('mouseup', () => handleMouseUp(point, { getSelected, stateRef, dragRef, defaultBrands, onBrandsChange }))

    if (isMobile) return

    node.on('mouseover', function () {
      const selection = d3.select(this)
      selection.select(`circle.${CLS.outer}`).transition().duration(100).attr('r', 10).style('opacity', 0.3)
      if (!getSelected().length) {
        selection.select(`g.${CLS.label}`).attr('opacity', 0.7)
        selection.select(`path.${CLS.pointPath}`).attr('opacity', 0.7)
      }
    })

    node.on('mouseout', function () {
      const selected = getSelected()
      const selection = d3.select(this)

      if (!selected.length) {
        selection.select(`circle.${CLS.outer}`).transition().duration(100).attr('r', 0).style('opacity', 0)
        selection.select(`g.${CLS.label}`).attr('opacity', 0)
        selection.select(`path.${CLS.pointPath}`).attr('opacity', 0)
        return
      }

      if (selected.includes(point.id)) {
        const r = point.id === stateRef.current.selectedSingle ? 10 : 6
        selection.select(`circle.${CLS.outer}`).transition().duration(500).attr('r', r).style('opacity', 1)
      } else {
        selection.select(`circle.${CLS.outer}`).transition().duration(100).attr('r', 6).style('opacity', 0)
      }
    })
  })
}

const detectMobile = () =>
  window.MobileDetect ? Boolean(new window.MobileDetect(window.navigator.userAgent).mobile()) : false

/**
 * Wire mouseup (selection toggle), hover, and label drag onto the drawn scene.
 * @param svgNode root <svg>
 * @param {{ points, defaultBrands:string[], getSelected:()=>string[],
 *   stateRef, dragRef, onBrandsChange:(next:string[])=>void }} ctx
 *   stateRef.current: { selected, selectedState, selectedSingle } toggle bookkeeping
 *   dragRef.current:  { dragging } guards mouseup against a finished drag
 */
export const attachInteractions = (svgNode, ctx) => {
  const root = d3.select(svgNode)
  attachDrag(root, ctx.dragRef)
  attachPointHandlers(root, { ...ctx, isMobile: detectMobile() })
}
